import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useLayoutEffect, useRef } from 'react';

import { isSessionGateRefusal } from '@/api/errors';
import { resetThumbnails } from '@/api/thumbnails';
import { useSessionContext } from '@/features/auth/session';

/**
 * The one place that tells the query cache a session transition happened.
 *
 * **Nothing used to.** `adopt()` in `features/auth/session.tsx` is the only exit from `locked`, and
 * it replaces the session union and nothing else — so every refusal the lock produced stayed exactly
 * where it was. A query refused with `AUTH_SESSION_IDLE_LOCKED` is never retried (the provider's
 * `retry` returns false for any `ApiError`) and never refetched on focus (unlocking an overlay in
 * the same visible tab fires no `visibilitychange`), so an administration panel replaced by that
 * refusal stayed replaced by it, on a terminal whose PIN had just been accepted. That is the defect
 * this component exists for.
 *
 * **It is rendered INSIDE `QueryProvider` rather than beside `SessionProvider`**, which is what lets
 * it hold both halves at once: context flows downward, so a component below both providers reads
 * `useSessionContext()` and `useQueryClient()` together. Reordering the providers would have been
 * the obvious fix and is not needed.
 *
 * **Two transitions, and they are not the same transition.**
 *
 * A change of ACCOUNT — a sign-out, a sign-in, or a hand-over at a locked terminal — must leave
 * nothing of the previous operator behind. Until now nothing cleared the cache on any of the three
 * (`switch-operator.tsx`'s `forget` is two state setters), and `gcTime` is TanStack's five-minute
 * default: a second operator signing in within that window and opening the same record was served
 * the first operator's DECRYPTED name, passport number, telephone and notes on first paint, with no
 * `person.view_full` row naming them for it. The register, the roll-call and the outside monitor
 * carry names and were cached the same way. That is a confidentiality and audit-attribution defect,
 * and it is why this runs at all.
 *
 * A RETURN from the lock is the same human at the same terminal, and must be surgical. Resetting
 * everything would re-read every open record, and `person.get` writes the `person.view_full` row
 * AD-37's exfiltration threshold counts — so an operator who unlocked a terminal twice would be
 * moved twice towards an alert about reading records they never opened. Only the queries the lock
 * actually broke are touched.
 *
 * **`useLayoutEffect`, not `useEffect`, and the difference is one frame of somebody else's data.** A
 * passive effect runs after the commit has painted. On a hand-over the incoming operator's tree
 * renders once against the outgoing operator's cache, and that frame reaches the screen. A layout
 * effect runs before the browser paints, so it does not.
 *
 * **`resetQueries`, not `clear`.** `clear()` is `queryCache.clear()` plus `mutationCache.clear()`,
 * and `queryCache.clear()` REMOVES queries — it does not refetch a mounted observer, which would
 * have left the new operator looking at a screen that was merely empty and dependent on an
 * incidental re-render to repopulate. `resetQueries` is documented and implemented as
 * `query.reset()` on every match followed by `refetchQueries({ type: 'active' })`: the cached data
 * is genuinely gone (`reset()` restores the initial state, whose `data` is `undefined`) AND every
 * mounted screen asks again. The mutation cache is cleared explicitly beside it, because
 * `resetQueries` does not touch it and a one-time password held in a settled mutation is exactly the
 * kind of thing that must not survive a hand-over.
 */
export function SessionCacheBridge(): ReactNode {
  const { state } = useSessionContext();
  const queries = useQueryClient();

  /**
   * What the previous commit's session was, as the two facts this decides on.
   *
   * A ref rather than state: this compares renders, it does not cause one. Seeded from the first
   * render so a terminal that boots straight into a restored session does not read that as an
   * account change and reset a cache that is already empty.
   */
  const previous = useRef<{ readonly status: string; readonly userId: string | null }>({
    status: state.status,
    userId: state.status === 'anonymous' ? null : state.session.user.id,
  });

  useLayoutEffect(() => {
    const now = {
      status: state.status,
      userId: state.status === 'anonymous' ? null : state.session.user.id,
    };
    const before = previous.current;
    previous.current = now;

    if (before.userId !== now.userId) {
      // A different human is at this terminal, or nobody is. Nothing of the last one may be read
      // from memory, and every screen still mounted has to ask again as whoever is here now.
      //
      // The photograph batches go FIRST. `resetQueries` re-asks every mounted view in the same call,
      // so forgetting the batches afterwards would reject the new operator's requests; forgetting
      // them before is what stops those requests joining a batch the previous session sent.
      resetThumbnails(queries);
      queries.getMutationCache().clear();
      void queries.resetQueries();
      return;
    }

    if ((before.status === 'locked' || before.status === 'expired') && now.status === 'active') {
      // Back in. Exactly the queries the lock refused, and nothing else: a query that answered
      // before the lock keeps its data and its ordinary staleness, and a record left open is not
      // re-read. `findAll` does not filter on active, so a panel the operator has since navigated
      // away from is reset here too, without a request, and asks again when it is next mounted.
      void queries.resetQueries({
        predicate: (query) => isSessionGateRefusal(query.state.error),
      });
    }
  }, [state, queries]);

  return null;
}
